import './App.css'
import { MainRouter } from './container/MainRouter'
import axios from "axios"
import { ThemeProvider } from './theme'
import { CookiesProvider } from 'react-cookie'
import { DataDogService, setDatadogAction, setDatadogError, setGlobalContext } from './services/datadog'
import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import Bowser from 'bowser'
import { isMobile } from './utils/utils'
import { usePreventIOSAutoZoom } from './utils/hooks/usePreventIOSAutoZoom'

function App() {
  axios.defaults.baseURL = process.env.REACT_APP_SIGN_API_URL
  usePreventIOSAutoZoom()

  const [signMetadata, setSignMetadata] = useState({})
  const [faceReconSdkLoaded, setFaceReconSdkLoaded] = useState(false);
  const faceSdkLoadingRef = useRef(false);

  const setSignMetadataCallback = useCallback((metadata) => {
    if (!signMetadata || Object.keys(signMetadata).length === 0) {
      setDatadogAction('Sign Metadata information fetched', metadata)
      setSignMetadata((prev) => ({ ...prev, ...metadata }))
    }
  }, [signMetadata])

  if (process.env.REACT_APP_ENV !== 'local') {
    DataDogService.init()
  }

  useEffect(() => {
    const dsScript = document.createElement('script')
    dsScript.src = 'https://ds.viewpkg.com/device-scan-1-1-0.js'
    dsScript.defer = true
    dsScript.onload = () => {
      setDatadogAction('device loaded');
    }
    dsScript.onerror = (error) => {
      setDatadogError('Device error occurred', { error });
    }
    document.body.appendChild(dsScript)

    return () => {
      document.body.removeChild(dsScript)
    }
  }, [])

  useEffect(() => {
    const ocrScript = document.createElement('script')
    ocrScript.src = 'https://ocr.caas.qitech.app/3-4-0/ocr.js'
    ocrScript.defer = true
    ocrScript.onload = () => {
      setDatadogAction('ocr loaded');
    }
    ocrScript.onerror = (error) => {
      setDatadogError('OCR error occurred', { error });
    }
    document.body.appendChild(ocrScript)

    return () => {
      document.body.removeChild(ocrScript)
    }
  }, [])

  useEffect(() => {
    const FACE_RECON_VERSION_COOKIE = 'qi_face_recon_sdk_version'
    const NEW_SDK_URL =  'https://facerecon.zaig.com.br/face-recognition-3-4-1.js'
    const OLD_SDK_URL =  'https://facerecon.zaig.com.br/face-recognition-3-3-0.js'
    const DEFAULT_NEW_VERSION_PERCENT = 100
    const FULL_NEW_SDK_COMPANY_KEYS = ['5d13e050-6d53-4bd5-8037-496fdffaef63', 'ca426354-c14f-4b1e-8044-45dbf298e4e2']
      .map((companyKey) => companyKey.trim().toLowerCase())
      .filter(Boolean)

    let faceReconScript = null

    const getStoredVersion = () => {
      try {
        const storedVersion = document.cookie
          .split('; ')
          .find((row) => row.startsWith(`${FACE_RECON_VERSION_COOKIE}=`))
          ?.split('=')[1]

        if (storedVersion === '3.4.1' || storedVersion === '3.3.0') {
          return storedVersion
        }
      } catch {
        return null
      }

      return null
    }

    let cleanupWarmupLoadListener = null
    let warmupLoginCheckInterval = null
    let hasPerformedWarmup = false
    let cancelled = false

    const getSignerHashFromPath = () => {
      const match = window.location.pathname.match(/^\/s\/([^/]+)/)
      return match ? match[1] : null
    }

    const getLoginHashFromQuery = () => {
      return new URLSearchParams(window.location.search).get('login_hash')
    }

    const fetchSignerCompanyKey = async () => {
      const signerHash = getSignerHashFromPath()
      if (!signerHash) {
        return null
      }

      try {
        const loginHash = getLoginHashFromQuery()
        const config = loginHash
          ? { headers: { Authorization: `Bearer ${loginHash}` } }
          : undefined
        const response = await axios.get(`/signer/hash/${signerHash}`, config)
        return response?.data?.signer_company_key || null
      } catch {
        return null
      }
    }

    const resolveNewVersionPercent = async () => {
      if (!FULL_NEW_SDK_COMPANY_KEYS.length) {
        return DEFAULT_NEW_VERSION_PERCENT
      }

      const signerCompanyKey = signMetadata?.signer_company_key || await fetchSignerCompanyKey();
      if (!signerCompanyKey) {
        return DEFAULT_NEW_VERSION_PERCENT
      }

      const normalizedCompanyKey = String(signerCompanyKey).trim().toLowerCase()
      if (FULL_NEW_SDK_COMPANY_KEYS.includes(normalizedCompanyKey)) {
        return 100
      } else {
        return DEFAULT_NEW_VERSION_PERCENT
      }
    }

    const runWarmupIfAvailable = (variant, scriptUrl) => {
      if (cancelled) {
        return
      }

      const warmupMedia = window?.ZaigWebFaceRecon?.WebFaceRecon?.warmupMedia
      if (typeof warmupMedia !== 'function') {
        setDatadogError('warmupMedia is not available after SDK load', { faceReconSdkVariant: variant, faceReconSdkUrl: scriptUrl })
        return
      }

      warmupMedia()
        .then((result) => {
          setDatadogAction('face recognition warmup success', {
            faceReconSdkVariant: variant,
            faceReconSdkUrl: scriptUrl,
            warmupResult: result
          })
        })
        .catch((error) => {
          setDatadogError('face recognition warmup error', {
            error,
            faceReconSdkVariant: variant,
            faceReconSdkUrl: scriptUrl
          })
        })
    }

    const hasLoggedInCookie = () => {
      try {
        return document.cookie
          .split('; ')
          .some((row) => row.startsWith('qisign_'))
      } catch {
        return false
      }
    }

    const triggerWarmupOnce = () => {
      if (hasPerformedWarmup) {
        return
      }

      hasPerformedWarmup = true
      runWarmupIfAvailable(variantRef, scriptUrlRef)
    }

    let variantRef = null
    let scriptUrlRef = null

    const startWarmupWhenAuthenticated = () => {
      if (hasLoggedInCookie()) {
        setDatadogAction('face recognition warmup scheduled (already logged)')
        if (document.readyState === 'complete') {
          triggerWarmupOnce()
          return
        }

        const onLoad = () => {
          triggerWarmupOnce()
        }

        window.addEventListener('load', onLoad, { once: true })
        cleanupWarmupLoadListener = () => {
          window.removeEventListener('load', onLoad)
        }
        return
      }

      warmupLoginCheckInterval = window.setInterval(() => {
        if (!hasLoggedInCookie()) {
          return
        }

        setDatadogAction('face recognition warmup scheduled (after login)')
        window.clearInterval(warmupLoginCheckInterval)
        warmupLoginCheckInterval = null
        triggerWarmupOnce()
      }, 1000)
    }

    const loadFaceReconScript = async () => {
      if (faceSdkLoadingRef.current || faceReconSdkLoaded) return;
      faceSdkLoadingRef.current = true;
      const NEW_VERSION_PERCENT = await resolveNewVersionPercent()
      if (cancelled) {
        faceSdkLoadingRef.current = false;
        return
      }

      const storedVersion = getStoredVersion()
      let selectedVersion;
      if (storedVersion) {
        selectedVersion = storedVersion;
      } else if (Math.random() * 100 < NEW_VERSION_PERCENT) {
        selectedVersion = '3.4.1';
      } else {
        selectedVersion = '3.3.0';
      }

      if (!storedVersion) {
        document.cookie = `${FACE_RECON_VERSION_COOKIE}=${selectedVersion}; path=/; max-age=7200; samesite=lax`
      }

      const scriptUrl = selectedVersion === '3.4.1' ? NEW_SDK_URL : OLD_SDK_URL
      const variant = selectedVersion
      scriptUrlRef = scriptUrl
      variantRef = variant

      faceReconScript = document.createElement('script')
      faceReconScript.src = scriptUrl
      faceReconScript.defer = true
      faceReconScript.onload = () => {
        setFaceReconSdkLoaded(true);
        faceSdkLoadingRef.current = false;
        setDatadogAction('face recognition loaded', { faceReconSdkVariant: variant, faceReconSdkUrl: scriptUrl })
        if (scriptUrl === NEW_SDK_URL && isMobile()) {
          startWarmupWhenAuthenticated()
        }
      }
      
      faceReconScript.onerror = (error) => {
        setDatadogError('Face recognition error occurred', { error, faceReconSdkVariant: variant, faceReconSdkUrl: scriptUrl })
        setFaceReconSdkLoaded(false);
        faceSdkLoadingRef.current = false;
      }
      document.body.appendChild(faceReconScript)
    }

    if (signMetadata?.face) {
      if (!faceReconSdkLoaded) {
        loadFaceReconScript()
      }
    } else if (Object.keys(signMetadata).length > 0) {
      setDatadogAction('Skipping face recognition script load, due to envelope not containing face step.')
    }

    return () => {
      cancelled = true
      if (cleanupWarmupLoadListener) {
        cleanupWarmupLoadListener()
      }
      if (warmupLoginCheckInterval) {
        window.clearInterval(warmupLoginCheckInterval)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signMetadata?.face])

  const [osInformation, setOsInformation] = useState(null);
  const [bowserOsInformation, setBowserOsInformation] = useState(null);
  const [browserInformation, setBrowserInformation] = useState(null);
  const [userIp, setUserIp] = useState(null);
  const [fetchedIpOnce, setFetchedIpOnce] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchIp = async () => {
      try {
        const response = await axios.get('https://api.ipify.org?format=json', { timeout: 5000 });
        if (!cancelled && response && response.data && response.data.ip) {
          setUserIp(String(response.data.ip));
          setDatadogAction('Fetched user public IP', { ip: String(response.data.ip) });
        }
      } catch (error) {
        setDatadogAction('Failed to fetch user public IP', { error: error });
      } finally {
        setFetchedIpOnce(true);
      }
    };
    fetchIp();
    const intervalId = setInterval(fetchIp, 60000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, []);

  const getOsInformation = async () => {
    try {
      const parser = Bowser.getParser(window.navigator.userAgent);
      const bowserOsInfo = parser.getOS();
      const bowserBrowserInfo = parser.getBrowser();
      
      const uaData = navigator.userAgentData;
      if (uaData && typeof uaData.getHighEntropyValues === 'function') {
        try {
          const ua = await uaData.getHighEntropyValues([
            'platform',
            'platformVersion',
            'model',
            'mobile'
          ]);
          const platform = String(ua.platform || '').toLowerCase();
          if (ua.platformVersion) {
            const major = parseInt(String(ua.platformVersion).split('.')[0], 10);
            if (!Number.isNaN(major)) {
              const osInfo = { name: platform, version: String(major) };
              setDatadogAction('OS Extracted information (UA-CH + Bowser)', { ...ua, os: osInfo, bowserOs: bowserOsInfo, browser: bowserBrowserInfo });
              setGlobalContext({ ...ua, os: osInfo, bowserOs: bowserOsInfo, browser: bowserBrowserInfo });
              return { os: osInfo, browser: bowserBrowserInfo, bowserOs: bowserOsInfo };
            }
          }
        } catch (e) {
          setDatadogError('UA-CH extraction failed', { error: e });
        }
      }

      setDatadogAction('OS Extracted information (Bowser only)', { bowserOs: bowserOsInfo, browser: bowserBrowserInfo });
      setGlobalContext({ bowserOs: bowserOsInfo, browser: bowserBrowserInfo });
      return { os: null, browser: bowserBrowserInfo, bowserOs: bowserOsInfo };
    } catch (error) {
      setDatadogError('[Startup] Failed to parse OS info with Bowser:', error);
      return { os: null, browser: null, bowserOs: null };
    }
  }
 
  useEffect(() => {
    let cancelled = false;
    const updateOs = async () => {
      const { os, browser, bowserOs } = await getOsInformation();
      if (cancelled) return;
      setOsInformation((prev) => {
        if (!prev) return os;
        const prevRaw = JSON.stringify(prev);
        const nextRaw = JSON.stringify(os);
        return prevRaw === nextRaw ? prev : os;
      });
      setBowserOsInformation((prev) => {
        if (!prev) return bowserOs;
        const prevRaw = JSON.stringify(prev);
        const nextRaw = JSON.stringify(bowserOs);
        return prevRaw === nextRaw ? prev : bowserOs;
      });
      setBrowserInformation((prev) => {
        if (!prev) return browser;
        const prevRaw = JSON.stringify(prev);
        const nextRaw = JSON.stringify(browser);
        return prevRaw === nextRaw ? prev : browser;
      });
    };
    updateOs();
    const intervalId = setInterval(updateOs, 10000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, []);

  const isInvalidOS = useMemo(() => {

    const os = osInformation ?? bowserOsInformation;
    if (!os || !os.name) return false;

    const isLinux = String(os.name).toLowerCase() === 'linux';
    setDatadogAction(`userIp (Ipify) - ${userIp}`, { ipifyIp: userIp });
    if (isLinux && fetchedIpOnce) {
      if (!userIp) return true;
      if (!['186.224.219.198', '201.6.229.166'].includes(String(userIp).trim())) {
        setDatadogAction('Linux outside of viplisted zones - Blocked', { ipifyIp: userIp });
        return true;
      }
    }


    if (!os.version) return false;

    const major = parseInt(String(os.version).split('.')[0], 10);
    if (Number.isNaN(major)) return false;
    
    const isAndroid = String(os.name).toLowerCase().includes('android');
    if (isAndroid && major < 9) {
      return true;
    }

    const isIOS = String(os.name).toLowerCase().includes('ios');
    if (isIOS && major < 15) {
      return true;
    }

    return false;
  
  }, [osInformation, bowserOsInformation, userIp, fetchedIpOnce]);

  const isInvalidBrowser = useMemo(() => {
    if (!browserInformation || !browserInformation.name || !browserInformation.version) return false;

    if (![
      'chrome',
      'safari',
      'samsung internet for android',
      'miui',
      'google search',
      'microsoft edge',
      'firefox',
      'opera'
    ].includes(browserInformation.name.toLowerCase())) return true;

    const isChromeMobile = String(browserInformation.name).toLowerCase().includes('chrome');
    if (!isChromeMobile) return false;
    const major = parseInt(String(browserInformation.version).split('.')[0], 10);
    if (Number.isNaN(major)) return false;
    return major < 121;
  }, [browserInformation]);

  return (
    <CookiesProvider>
      <ThemeProvider>
        <MainRouter
          isInvalidOS={isInvalidOS}
          isInvalidBrowser={isInvalidBrowser}
          signMetadata={signMetadata}
          setSignMetadata={setSignMetadataCallback}
        />
      </ThemeProvider>
    </CookiesProvider>
  )
}

export default App
